// Локальный сейв dg_save_v1 (client-design §5.2) — зеркало клейм-блоба §5.2 бэкенда.
// Гость живёт только здесь; после входа блоб клеймится (мёрж на сервере) и остаётся кэшем.
// Записи copy-on-write: ссылка блоба меняется на каждую правку, поэтому подписчик
// (subscribeSave) может использовать loadSave() как снапшот useSyncExternalStore.

import { getAnonId } from '@/api/anon';
import { createPersistedStore } from '@/meta/persistedStore';

export interface SaveLevelEntry {
  rating: number;
  status: string;
  bestDurationMs: number | null;
  objectives: { id: string; done: boolean }[];
}

export interface SaveBlob {
  saveVersion: 1;
  anonId: string;
  levels: Record<string, SaveLevelEntry>;
  claimedAt?: string;
  updatedAt: string;
}

function sanitize(raw: unknown): SaveBlob {
  const parsed = raw as Partial<SaveBlob> | undefined;
  if (parsed && parsed.saveVersion === 1) return parsed as SaveBlob;
  // неизвестная версия схемы — read-only политика §5.3; начинаем чистый (v1 — первая)
  return {
    saveVersion: 1,
    anonId: getAnonId(),
    levels: {},
    updatedAt: new Date().toISOString(),
  };
}

const store = createPersistedStore<SaveBlob>('dg_save_v1', sanitize);

export function loadSave(): SaveBlob {
  return store.get();
}

/** Подписка на изменения сейва (реактивность готова; экраны переезжают в Ф3+). */
export function subscribeSave(listener: () => void): () => void {
  return store.subscribe(listener);
}

function persist(blob: SaveBlob): SaveBlob {
  return store.set({ ...blob, updatedAt: new Date().toISOString() });
}

/** Что НОВОГО принёс результат: сколько звёзд добавилось сверх прежнего рекорда и
 *  открылся ли следующий уровень (линейная разблокировка: его открывает ПЕРВОЕ
 *  прохождение этого).
 *
 *  Отдельная функция, а не поле в `applyResult`, ровно по одной причине: считать надо
 *  ДО мёржа, пока прежний рекорд ещё виден. После него разницы уже нет — мёрж
 *  повышающий, и «три звезды впервые» неотличимо от «три звезды в сотый раз».
 *  Из-за этого мету и нельзя было озвучить, не заведя такой функции: код нигде не
 *  отличал «звезда начислена» от «звезда нарисована». */
export interface ProgressGain { starsGained: number; firstCompletion: boolean }

export function gainFor(levelId: string, result: { rating: number; status: string }): ProgressGain {
  const prev = loadSave().levels[levelId];
  if (result.status !== 'completed') return { starsGained: 0, firstCompletion: false };
  return {
    starsGained: Math.max(0, result.rating - (prev?.rating ?? 0)),
    firstCompletion: (prev?.status ?? '') !== 'completed',
  };
}

/** Повышающий мёрж результата уровня (max-rating / OR-objectives / min-duration) — зеркало §4.5. */
export function applyResult(levelId: string, result: {
  rating: number; status: string; durationMs: number;
  objectives: { id: string; done: boolean }[];
}): SaveBlob {
  const blob = loadSave();
  const prev = blob.levels[levelId];
  const completed = result.status === 'completed';

  const objectives = new Map<string, boolean>();
  for (const o of prev?.objectives ?? []) objectives.set(o.id, o.done);
  for (const o of result.objectives) objectives.set(o.id, (objectives.get(o.id) ?? false) || o.done);

  const entry: SaveLevelEntry = {
    rating: Math.max(prev?.rating ?? 0, completed ? result.rating : 0),
    status: rank(result.status) > rank(prev?.status ?? 'quit') ? result.status : prev?.status ?? result.status,
    bestDurationMs: completed
      ? minNullable(prev?.bestDurationMs ?? null, result.durationMs > 0 ? result.durationMs : null)
      : prev?.bestDurationMs ?? null,
    objectives: [...objectives.entries()].map(([id, done]) => ({ id, done })),
  };
  return persist({ ...blob, levels: { ...blob.levels, [levelId]: entry } });
}

export function starsFor(levelId: string): number {
  return loadSave().levels[levelId]?.rating ?? 0;
}

export function hasUnclaimedProgress(): boolean {
  const blob = loadSave();
  return !blob.claimedAt && Object.keys(blob.levels).length > 0;
}

export function markClaimed(): void {
  persist({ ...loadSave(), claimedAt: new Date().toISOString() });
}

/** Полный снапшот для POST /me/progress/claim (тело = блоб как есть, §5.1). */
export function claimPayload(): SaveBlob {
  return loadSave();
}

/** Дев-чит/логика тестов: полный сброс локального сейва. */
export function resetSave(): void {
  store.reset();
}

function rank(s: string): number {
  return s === 'completed' ? 3 : s === 'failed' ? 2 : 1;
}
function minNullable(a: number | null, b: number | null): number | null {
  return a === null ? b : b === null ? a : Math.min(a, b);
}
